import { ChangeDetectionStrategy, Component, ViewEncapsulation, computed, inject, input } from '@angular/core';
import { CodeInvoiceStatus, FinanceSecurityRight, ItemLinkEntity } from '@jms/model';
import { RightsService } from '@jms/utils/util.permissions';
import { invoicingForcesZeroSell, payableForcesZeroBuy } from '@workbuddy/surfaces/embedded-item';

/** One row of the card: leading icon + label, then the answer or document reference (+ link when permitted). */
interface ItemFinanceRow {
  icon: string;
  label: string;
  value: string;
  url?: string;
}

/**
 * "Financial Documents" hover-card content for the board's finance counter. Every row is always
 * drawn — an unlinked slot reads "Nil" — so two lines never compare different sets of rows.
 *
 * Billable and Payable use the shared exclusion rules, not the stored value, so the card cannot
 * disagree with the money cells: absent status is billable, only an explicit `false` is not payable.
 *
 * The link arrow needs the matching finance right; without it the reference stays visible, unlinked.
 * Content only — the dark surface is `wb-dark-tooltip` (PrimeNG tooltip, appended to body).
 */
@Component({
  selector: 'item-finance-card',
  template: `
    <div class="finance-card">
      <div class="card-title">Financial Documents</div>

      <div class="card-list">
        @for (row of $rows(); track row.label) {
          <div class="card-row">
            <span class="card-label">
              <span class="card-icon"><i [class]="row.icon"></i></span>
              <span>{{ row.label }}</span>
            </span>

            @if (row.url) {
              <a class="card-value link" [href]="row.url" target="_blank" rel="noopener noreferrer" (click)="$event.stopPropagation()">
                <span>{{ row.value }}</span>
                <i class="fa-regular fa-arrow-up-right"></i>
              </a>
            } @else {
              <span class="card-value">{{ row.value }}</span>
            }
          </div>
        }
      </div>
    </div>
  `,
  styleUrls: ['./item.finance.card.component.less'],
  standalone: true,
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ItemFinanceCardComponent {

  private readonly rightsService = inject(RightsService);

  private readonly canAccessPurchaseOrder = this.rightsService.hasRights(FinanceSecurityRight.CanAccessPurchaseOrder);
  private readonly canAccessBill = this.rightsService.hasRights(FinanceSecurityRight.CanAccessBills);
  private readonly canAccessInvoice = this.rightsService.hasRights(FinanceSecurityRight.CanAccessInvoices);

  public readonly $purchaseOrder = input<ItemLinkEntity | null>(null, { alias: 'purchaseOrder' });
  public readonly $bill = input<ItemLinkEntity | null>(null, { alias: 'bill' });
  public readonly $invoice = input<ItemLinkEntity | null>(null, { alias: 'invoice' });
  public readonly $invoicingStatus = input<CodeInvoiceStatus | null>(null, { alias: 'invoicingStatus' });
  /** Null on items predating the field; absent reads as payable. */
  public readonly $payable = input<boolean | null>(null, { alias: 'payable' });
  public readonly $suppliedBySubcontractor = input(false, { alias: 'suppliedBySubcontractor' });

  /** Fixed six, in the specified order. Bill and Invoice share the receipt icon per design. */
  protected readonly $rows = computed<ItemFinanceRow[]>(() => {

    const invoicingStatus = this.$invoicingStatus();

    return [
      { icon: 'fa-regular fa-circle-dollar', label: 'Billable', value: invoicingStatus && invoicingForcesZeroSell(invoicingStatus) ? 'No' : 'Yes' },
      { icon: 'fa-regular fa-money-check-dollar', label: 'Payable', value: payableForcesZeroBuy(this.$payable()) ? 'No' : 'Yes' },
      { icon: 'fa-regular fa-circle-user', label: 'Supplied By Contractor', value: this.$suppliedBySubcontractor() ? 'Yes' : 'No' },
      this.documentRow('fa-regular fa-receipt', 'Invoice', this.$invoice(), this.canAccessInvoice, '/finance/invoices'),
      this.documentRow('fa-regular fa-receipt', 'Bill', this.$bill(), this.canAccessBill, '/finance/bills'),
      this.documentRow('fa-regular fa-file-lines', 'Purchase Order', this.$purchaseOrder(), this.canAccessPurchaseOrder, '/finance/purchase-order'),
    ];
  });

  /** An unlinked slot still earns its row: "Nil" is the answer, and there is no link to offer. */
  private documentRow(icon: string, label: string, document: ItemLinkEntity | null, canAccess: boolean, route: string): ItemFinanceRow {

    if (!document) return { icon, label, value: 'Nil' };

    return { icon, label, value: document.name ?? '', url: canAccess && document._id ? `${route}/${encodeURIComponent(document._id)}` : undefined };
  }
}
